import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import translations from '../utils/data';
import { Helmet } from 'react-helmet';

const COUNTRY_CURRENCY_MAP = {
    'India': { currency: 'INR', symbol: '₹', rate: 1 }
};
const DEFAULT_COUNTRY = 'India';
const DEFAULT_CURRENCY = COUNTRY_CURRENCY_MAP[DEFAULT_COUNTRY];
const VALID_PROMO_CODE = "KARUNGALI";

// API configuration
const API_BASE_URL = "https://sacredrelm-api.vercel.app/api"; 
// https://sacredrelm-api.vercel.app


const Checkout = ({ currentLang }) => {
    const t = translations || translations.ENGLISH;
    const location = useLocation();
    const navigate = useNavigate();
    const [orderDetails, setOrderDetails] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [currentCurrency, setCurrentCurrency] = useState(DEFAULT_CURRENCY);
    const [convertedAmount, setConvertedAmount] = useState(0);
    const [promoCode, setPromoCode] = useState("");
    const [isPromoApplied, setIsPromoApplied] = useState(false);
    const [orderNumber, setOrderNumber] = useState(1);
    const [paymentMode, setPaymentMode] = useState("");
    const [showPromoToast, setShowPromoToast] = useState(false);
    const [promoToastMessage, setPromoToastMessage] = useState('');
    
    // New state variables for payment processing
    const [processingPayment, setProcessingPayment] = useState(false);
    const [razorpayOrderId, setRazorpayOrderId] = useState(null);
    const [paymentId, setPaymentId] = useState(null);
    const [orderCreationError, setOrderCreationError] = useState(null); 
    const [verificationError, setVerificationError] = useState(null);
    const [isPageLeaving, setIsPageLeaving] = useState(false);
    const [hasFormData, setHasFormData] = useState(false);
    const [orderSuccessDetails, setOrderSuccessDetails] = useState(null);
    const [paymentInitiated, setPaymentInitiated] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('online');
    const [processingCodPrepay, setProcessingCodPrepay] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otp, setOtp] = useState('');
    const [otpVerified, setOtpVerified] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [otpSending, setOtpSending] = useState(false);
    const [otpVerifying, setOtpVerifying] = useState(false);
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [showPhoneConfirmModal, setShowPhoneConfirmModal] = useState(false);
    const [isEditingPhone, setIsEditingPhone] = useState(false);
    const [tempPhone, setTempPhone] = useState('');
    // Feature toggle for COD OTP
    const ENABLE_COD_OTP = true; // set true to re-enable OTP later

    const BYPASS_OTP_ON_ERROR = true;

    useEffect(() => {
        if (orderDetails) {
            // If orderDetails has a paymentMode, use it. Otherwise, default to "online".
            setPaymentMode(orderDetails.paymentMode || "online");
        }
        if (!selectedPaymentMethod) {
            setSelectedPaymentMethod('online');
        }
    }, [orderDetails, selectedPaymentMethod]);

    useEffect(() => {
        // Simulate fetching the latest order number from the backend
        const latestOrderNumber = localStorage.getItem("orderNumber") || 1;
        setOrderNumber(parseInt(latestOrderNumber, 10));
    }, []);

    const incrementOrderNumber = () => {
        const nextOrderNumber = orderNumber + 1;
        setOrderNumber(nextOrderNumber);
        localStorage.setItem("orderNumber", nextOrderNumber); // Persist order number locally
    };

    const sendOtp = async () => {
        if (!formData.phone) {
            setOtpError("Phone number is required to send OTP");
            return;
        }
        setOtpSending(true);
        setOtpError('');
        try {
            const response = await fetch(`${API_BASE_URL}/generate-otp`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({ phoneNumber: formData.phone })
            });
            if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();
            if (data.success) {
            setOtpSent(true);
            } else {
            // Backend responded but with failure
            throw new Error(data.message || "Failed to send OTP");
            }
        } catch (error) {
            // BYPASS: mark OTP as verified and place COD order
            if (BYPASS_OTP_ON_ERROR) {
            setOtpVerified(true);
            setOtpError('');
            setShowOtpModal(false);
            setShowPhoneConfirmModal(false);

            // Only auto-place when COD is selected and billing is complete
            if (
                selectedPaymentMethod === 'cod' &&
                isBillingDetailsComplete() &&
                !processingPayment
            ) {
                try {
                await handleCashOnDelivery();
                } catch (e) {
                // optional: surface a minimal error
                setFormErrors(prev => ({ ...prev, submit: 'Order processing error' }));
                }
            }
            return; // stop normal error flow
            }

            // If bypass is off, show a normal error
            setOtpError("Error sending OTP");
        } finally {
            setOtpSending(false);
        }
    };

    const verifyOtp = async () => {
        if (!otp) {
            setOtpError("Please enter the OTP");
            return;
        }
        setOtpVerifying(true);
        setOtpError('');
        try {
            const response = await fetch(`${API_BASE_URL}/verify-otp`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({ phoneNumber: formData.phone, otp })
            });

            if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();
            if (data.success) {
            setOtpVerified(true);
            setOtpError('');
            setShowOtpModal(false);
            } else {
            throw new Error(data.message || "Invalid OTP");
            }
        } catch (error) {
            if (BYPASS_OTP_ON_ERROR) {
            setOtpVerified(true);
            setOtpError('');
            setShowOtpModal(false);
            setShowPhoneConfirmModal(false);

            if (
                selectedPaymentMethod === 'cod' &&
                isBillingDetailsComplete() &&
                !processingPayment
            ) {
                try {
                await handleCashOnDelivery();
                } catch (e) {
                setFormErrors(prev => ({ ...prev, submit: 'Order processing error' }));
                }
            }
            return;
            }
            setOtpError("Error verifying OTP");
        } finally {
            setOtpVerifying(false);
        }
    };

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        companyName: '',
        country: DEFAULT_COUNTRY,
        streetAddress: '',
        apartment: '',
        townCity: '',
        phone: '',
        email: '',
        paymentMode: ""
    });
    const hasKarungaliMala = () => {
        const match = (name) => {
            const s = (name || '').toLowerCase();
            return s.includes('karungali mala') || s.includes('karungali twin') || (s.includes('karungali') && s.includes('mala'));
        };
        if (Array.isArray(verifiedProduct) && verifiedProduct.some(p => match(p?.name))) return true;
        if (match(orderDetails?.productName)) return true;
        if (Array.isArray(orderDetails?.items) && orderDetails.items.some(i => match(i?.name))) return true;
        return false;
    };
    
    useEffect(() => {
        if (orderDetails && orderDetails.totalAmount) {
            const foundCurrency = COUNTRY_CURRENCY_MAP[formData.country] || DEFAULT_CURRENCY;
            setCurrentCurrency(foundCurrency);

            let baseAmount = Number(orderDetails.totalAmount); // Amount in INR

            // if (isPromoApplied) {
            //     baseAmount *= 0.5; // 50% promo discount
            // }
            if (selectedPaymentMethod === 'online' && isOnlineDiscountAllowed(orderDetails, verifiedProduct)) {
                baseAmount *= 0.95; // 5% online payment discount
            }

            // Add Shipping charge based on product price: 0 for online, 120 for COD if product >= 1590, 90 for COD if product < 1590
            let shippingChargeINR = 0;
            if (selectedPaymentMethod === 'cod') {
                const originalProductPrice = Number(orderDetails.totalAmount);
                shippingChargeINR = originalProductPrice >= 1590 ? 120 : 90;
            }

            const amountWithShipping = baseAmount + shippingChargeINR;

            // Convert with currency rate
            const convertedValue = (amountWithShipping * foundCurrency.rate).toFixed(2);
            setConvertedAmount(convertedValue);
        }
    }, [formData.country, orderDetails, isPromoApplied, selectedPaymentMethod]);


    // Add new state for product verification
    const [verifiedProduct, setVerifiedProduct] = useState(null);

    // Add useEffect to verify product details
    useEffect(() => {
        if (orderDetails && !verifiedProduct) { // Add condition to prevent infinite loop
            // Verify product details from location state
            const productInfo = orderDetails.items ?
                orderDetails.items.map(item => ({
                    name: item.name,
                    quantity: item.quantity || 1,
                    price: Number(item.price) || Number(item.cost) || 0, // Handle both price and cost
                })) :
                [{
                    name: orderDetails.productName,
                    quantity: orderDetails.quantity || 1,
                    price: Number(orderDetails.unitPrice) || Number(orderDetails.cost) || 0,
                }];
                
                setVerifiedProduct(productInfo);
                
                // Calculate and update total amount
                const totalAmount = productInfo.reduce((total, item) => {
                    return total + (item.price * item.quantity);
                }, 0);
                
                if (totalAmount !== orderDetails.totalAmount) {
                    setOrderDetails(prev => ({
                    ...prev,
                    totalAmount: totalAmount
                }));
            }
        }
    }, [orderDetails, verifiedProduct]); // Add verifiedProduct to dependencies

    // Update currency and convert amount when country changes
    useEffect(() => {
        if (orderDetails && orderDetails.totalAmount) {
            const foundCurrency = COUNTRY_CURRENCY_MAP[formData.country] || DEFAULT_CURRENCY;
            setCurrentCurrency(foundCurrency);

            // Ensure totalAmount is a number
            let baseAmount = Number(orderDetails.totalAmount); // Amount in INR
            

            const convertedValue = (baseAmount * foundCurrency.rate).toFixed(2);
            setConvertedAmount(convertedValue);
        }
    }, [formData.country, orderDetails, isPromoApplied]); // Specific dependencies

    // Original useEffects for initialization and script loading...
    useEffect(() => {
        if (!location.state) {
            navigate('/');
            return;
        }
        setOrderDetails(location.state);

        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, [location.state, navigate]);

    const validateForm = () => {
        const errors = {};
        if (!formData.firstName.trim()) errors.firstName = 'First name is required';
        if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
        if (!formData.country.trim()) errors.country = 'Country is required';
        if (!formData.streetAddress.trim()) errors.streetAddress = 'Street address is required';
        if (!formData.townCity.trim()) errors.townCity = 'Town/City is required';

        if (!formData.phone.trim()) {
            errors.phone = 'Phone number is required';
        } else if (!/^\d{10}$/.test(formData.phone)) {
            errors.phone = 'Phone number must be exactly 10 digits';
        }

        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Email is invalid';
        }
        return errors;
    };

    useEffect(() => {
    if (orderDetails) {
        const foundCurrency = COUNTRY_CURRENCY_MAP[formData.country] || DEFAULT_CURRENCY;
        setCurrentCurrency(foundCurrency);

        let baseAmount = orderDetails.totalAmount; // Amount in INR
        const convertedValue = (baseAmount * foundCurrency.rate).toFixed(2);
        setConvertedAmount(convertedValue);
    }
    }, [formData.country, orderDetails]); // remove isPromoApplied

    const handlePromoCodeApply = () => {
        const normalized = promoCode.trim().toUpperCase();

        if (normalized === VALID_PROMO_CODE && hasKarungaliMala()) {
            setIsPromoApplied(true);                  // now means “gift added”, not discount
            setFormErrors(prev => ({ ...prev, promoCode: '' }));
            setPromoToastMessage('Great news! Karungali bracelet is free with your Karungali Mala.');
            setShowPromoToast(true);
            setTimeout(() => setShowPromoToast(false), 5000);
            return;
        }

        if (normalized === VALID_PROMO_CODE && !hasKarungaliMala()) {
            setIsPromoApplied(false);
            setFormErrors(prev => ({
            ...prev,
            promoCode: 'This promo applies only when Karungali Mala is in your cart.'
            }));
            return;
        }

        setIsPromoApplied(false);
        setFormErrors(prev => ({ ...prev, promoCode: 'Invalid promo code' }));
    };


    const PromoToast = () => {
        if (!showPromoToast) return null;
        return (
            <div className="fixed bottom-4 right-4 z-50">
            <div className="bg-green-600 text-white rounded-lg shadow-lg px-4 py-3 flex items-start space-x-3 max-w-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mt-0.5 text-white" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-3.121-3.121a1 1 0 111.414-1.414L8.414 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                <p className="font-semibold">Offer applied</p>
                <p className="text-sm opacity-90">{promoToastMessage}</p>
                </div>
                <button
                type="button"
                onClick={() => setShowPromoToast(false)}
                aria-label="Close"
                className="ml-2 text-white/80 hover:text-white"
                >
                ×
                </button>
            </div>
            </div>
        );
    };

    // Check if basic customer information is filled
    useEffect(() => {
        if (formData.email && formData.firstName && formData.phone) {
            setHasFormData(true);
        } else {
            setHasFormData(false);
        }
    }, [formData.email, formData.firstName, formData.phone]);

    // Function to check if all required billing details are complete
    const isBillingDetailsComplete = () => {
        return (
            formData.firstName?.trim() &&
            formData.lastName?.trim() &&
            formData.country?.trim() &&
            formData.streetAddress?.trim() &&
            formData.townCity?.trim() &&
            formData.phone?.trim() &&
            formData.email?.trim() &&
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && // Basic email validation
            /^\d{10,15}$/.test(formData.phone.replace(/[^\d]/g, '')) // Basic phone validation
        );
    };

    // API service functions
    const createRazorpayOrder = async () => {
        try {
            setProcessingPayment(true);
            setOrderCreationError(null);
            
            const response = await fetch(`${API_BASE_URL}/create-order`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    amount: parseFloat(convertedAmount),
                    currency: currentCurrency.currency,
                    receipt: `receipt_${orderNumber}_${Date.now()}`,
                    notes: {
                        customerName: `${formData.firstName} ${formData.lastName}`,
                        customerEmail: formData.email,
                        customerPhone: formData.phone,
                        productDetails: JSON.stringify(verifiedProduct)
                    }
                }),
            });

            const data = await response.json();
            
            if (!data.success) {
                throw new Error(data.message || 'Failed to create order');
            }
            
            setRazorpayOrderId(data.order.id);
            return {
                orderId: data.order.id,
                key: data.key
            };
        } catch (error) {
            console.error('Error creating Razorpay order:', error);
            setOrderCreationError(error.message);
            setProcessingPayment(false);
            return null;
        }
    };

    const verifyRazorpayPayment = async (paymentData) => {
        try {
            setVerificationError(null);
            
            const response = await fetch(`${API_BASE_URL}/verify-payment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(paymentData),
            });

            const data = await response.json();
            
            if (!data.success) {
                throw new Error(data.message || 'Payment verification failed');
            }
            
            setPaymentId(data.paymentId);
            return true;
        } catch (error) {
            console.error('Error verifying payment:', error);
            setVerificationError(error.message);
            return false;
        }
    };

    const sendOrderConfirmationEmail = async () => {
        try {
            // Format products data
            const products = verifiedProduct || [];
            
            const emailData = {
                customerEmail: formData.email,
                orderDetails: {
                    orderNumber,
                    products,
                    totalAmount: convertedAmount,
                    currency: currentCurrency.symbol,
                    paymentMethod: selectedPaymentMethod === 'online' ? 'Online Payment (Razorpay)' : 'Cash on Delivery',
                    paymentId: paymentId || 'N/A'
                },
                customerDetails: {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phone: formData.phone,
                    email: formData.email,
                    address: formData.streetAddress,
                    apartment: formData.apartment,
                    city: formData.townCity,
                    country: formData.country
                }
            };
            console.log("Email Data:", emailData);

            const response = await fetch(`${API_BASE_URL}/send-order-confirmation`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(emailData),
            });

            const data = await response.json();
            if (!data.success) {
                console.warn('Failed to send order confirmation email:', data.message);
            }
            
            return data.success;
        } catch (error) {
            console.error('Error sending order confirmation email:', error);
            return false;
        }
    };

    const sendAbandonedCartEmail = useCallback(async () => {
        // Only proceed if we have enough customer information
        if (!hasFormData) return;
        
        try {
            // Format products data
            const products = verifiedProduct || [];
            
            const emailData = {
                customerEmail: formData.email,
                orderDetails: {
                    orderNumber,
                    products,
                    totalAmount: convertedAmount,
                    currency: currentCurrency.symbol
                },
                customerDetails: {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phone: formData.phone,
                    email: formData.email,
                    address: formData.streetAddress,
                    apartment: formData.apartment,
                    city: formData.townCity,
                    country: formData.country
                }
            };

            const response = await fetch(`${API_BASE_URL}/send-abandoned-order-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(emailData),
            });

            const data = await response.json();
            if (!data.success) {
                console.warn('Failed to send abandoned cart email:', data.message);
            }
            
            return data.success;
        } catch (error) {
            console.error('Error sending abandoned cart email:', error);
            return false;
        }
    }, [hasFormData, verifiedProduct, formData, orderNumber, convertedAmount, currentCurrency]);

    // Set up abandoned cart handler
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasFormData && !paymentSuccess && !isSubmitting) {
                sendAbandonedCartEmail();
                // The browser will display a confirmation dialog
                e.preventDefault();
                e.returnValue = "";
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasFormData, paymentSuccess, isSubmitting, sendAbandonedCartEmail]);

    // Handle payment cancellation/abandonment
    const handlePaymentCancellation = async () => {
        try {
            console.log('Payment cancelled by user, sending abandoned cart email...');
            
            // Only send abandoned cart email if we have customer information and payment was initiated
            if (hasFormData && paymentInitiated) {
                // Add a small delay to ensure this is a genuine abandonment, not accidental close
                setTimeout(async () => {
                    try {
                        await sendAbandonedCartEmail();
                        console.log('Abandoned cart email sent successfully');
                    } catch (error) {
                        console.error('Error sending abandoned cart email in timeout:', error);
                    }
                }, 2000); // 2 second delay
            }
            
            // Reset payment initiated state
            setPaymentInitiated(false);
        } catch (error) {
            console.error('Error sending abandoned cart email:', error);
        }
    };

    // Handle Razorpay payment
    const handleRazorpayPayment = async () => {
        try {
            // Set payment initiated flag
            setPaymentInitiated(true);
            
            // Create order first
            const orderData = await createRazorpayOrder();
            
            if (!orderData) {
                throw new Error('Failed to create payment order');
            }
            
            const { orderId, key } = orderData;

            // Initialize Razorpay options
            const options = {
                key,
                amount: parseFloat(convertedAmount) * 100, // Amount in smallest currency unit
                currency: currentCurrency.currency,
                name: 'Your Store Name', // Replace with your actual store name
                description: 'Purchase',
                order_id: orderId,
                handler: function(response) {
                    handlePaymentSuccess(response);
                },
                prefill: {
                    name: `${formData.firstName} ${formData.lastName}`,
                    email: formData.email,
                    contact: formData.phone
                },
                notes: {
                    address: formData.streetAddress
                },
                theme: {
                    color: '#3399cc'
                },
                modal: {
                    ondismiss: function() {
                        setProcessingPayment(false);
                        console.log('Payment window closed without completing payment');
                        // Send abandoned cart email when user cancels payment
                        handlePaymentCancellation();
                    }
                }
            };
            
            // Open Razorpay checkout
            const paymentObject = new window.Razorpay(options);
            paymentObject.open();
        } catch (error) {
            console.error('Error initializing Razorpay payment:', error);
            setFormErrors(prev => ({
                ...prev,
                submit: `Payment initialization failed: ${error.message}`
            }));
            setProcessingPayment(false);
            setPaymentInitiated(false); // Reset payment initiated state on error
        }
    };

    // Handle successful payment
    const handlePaymentSuccess = async (response) => {
        try {
            // Verify payment with backend
            const paymentData = {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
            };
            
            const verified = await verifyRazorpayPayment(paymentData);
            
            if (!verified) {
                throw new Error('Payment verification failed');
            }
            
            // Set payment ID
            setPaymentId(response.razorpay_payment_id);
            
            // Send order confirmation email
            await sendOrderConfirmationEmail();
            
            // Create order details object to pass to thank you page
            const orderData = {
                orderNumber: orderNumber,
                paymentId: response.razorpay_payment_id,
                amount: convertedAmount,
                currency: currentCurrency.symbol,
                paymentMethod: 'Online Payment'
            };
            
            // Update order number in localStorage
            incrementOrderNumber();
            setProcessingPayment(false);
            setPaymentInitiated(false); // Reset payment initiated state on success
            
            // Navigate to thank you page with order details
            navigate('/thank-you', { state: { orderData } });
            
        } catch (error) {
            console.error('Error processing payment:', error);
            setFormErrors(prev => ({
                ...prev,
                submit: `Payment processing error: ${error.message}`
            }));
            setProcessingPayment(false);
            setPaymentInitiated(false); // Reset payment initiated state on payment processing error
        }
    };

    // Handle Cash on Delivery
    const handleCashOnDelivery = async () => {
        try {
            setProcessingPayment(true);
            
            // Generate a pseudo order/payment ID for COD
            const codOrderId = `COD_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
            setPaymentId(codOrderId);
            
            // Send order confirmation email
            await sendOrderConfirmationEmail();
            
            // Create order details object to pass to thank you page
            const orderData = {
                orderNumber: orderNumber,
                paymentId: codOrderId,
                amount: convertedAmount,
                currency: currentCurrency.symbol,
                paymentMethod: 'Cash on Delivery'
            };
            
            // Update order number in localStorage
            incrementOrderNumber();
            setProcessingPayment(false);
            
            // Navigate to thank you page with order details
            navigate('/thank-you', { state: { orderData } });
            
        } catch (error) {
            console.error('Error processing COD order:', error);
            setFormErrors(prev => ({
                ...prev,
                submit: `Order processing error: ${error.message}`
            }));
            setProcessingPayment(false);
        }
    };

    // Updated form submission handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (ENABLE_COD_OTP && selectedPaymentMethod === 'cod' && !otpVerified) {
            setOtpError("Please verify OTP for Cash on Delivery");
            return;
        }
        // Validate form
        const errors = validateForm();
        setFormErrors(errors);
        
        if (Object.keys(errors).length > 0) {
            console.log('Form has errors:', errors);
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            if (selectedPaymentMethod === 'online') {
                await handleRazorpayPayment();
            } else if (selectedPaymentMethod === 'cod') {
                await handleCashOnDelivery();
            }
        } catch (error) {
            console.error('Error submitting order:', error);
            setFormErrors(prev => ({
                ...prev,
                submit: `Order submission failed: ${error.message}`
            }));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Add handleInputChange function
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear any errors for this field when user is typing
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Add handleRemoveProduct function
    const handleRemoveProduct = (productIndex) => {
        if (!verifiedProduct) return;
        
        // Make a copy of the products array without the removed item
        const updatedProducts = verifiedProduct.filter((_, index) => index !== productIndex);
        
        if (updatedProducts.length === 0) {
            // If no products left, navigate back to cart
            navigate('/shop');
            return;
        }
        
        // Calculate new total amount
        const newTotalAmount = updatedProducts.reduce((total, item) => {
            return total + (item.price * item.quantity);
        }, 0);
        
        // Update the verified product state
        setVerifiedProduct(updatedProducts);
        
        // Update order details with new product list and total
        setOrderDetails(prev => ({
            ...prev,
            items: updatedProducts,
            totalAmount: newTotalAmount
        }));
    };

    // Checkout steps indicator component
    const CheckoutSteps = () => {
        return (
            <div className="mb-8 px-4 py-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                <div className="flex items-center justify-between">
                    <div className="flex flex-col items-center">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-600 text-white">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3z" />
                            </svg>
                        </div>
                        <span className="text-sm font-medium mt-2 text-gray-700">Cart</span>
                    </div>
                    <div className="flex-1 h-0.5 mx-2 bg-blue-600"></div>
                    <div className="flex flex-col items-center">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-600 text-white">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <span className="text-sm font-medium mt-2 text-gray-700">Billing</span>
                    </div>
                    <div className="flex-1 h-0.5 mx-2 bg-gray-300"></div>
                    <div className="flex flex-col items-center">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-300 text-white">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <span className="text-sm font-medium mt-2 text-gray-500">Payment</span>
                    </div>
                    <div className="flex-1 h-0.5 mx-2 bg-gray-300"></div>
                    <div className="flex flex-col items-center">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-300 text-white">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <span className="text-sm font-medium mt-2 text-gray-500">Confirmation</span>
                    </div>
                </div>
            </div>
        );
    };

    // OTP Modal Component
    const OtpModal = () => {
        const otpRef = React.useRef(null);

        React.useEffect(() => {
        // Only focus on mount or when modal shows up
        if (showOtpModal && otpRef.current) {
            otpRef.current.focus();
        }
        }, [showOtpModal]);

        if (!showOtpModal) return null;
        const handleOtpChange = (e) => {
            let value = e.target.value;
            // Only digits, max length 4
            value = value.replace(/\D/g, "").slice(0, 4);
            setOtp(value);
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Verify Phone Number</h3>
                        <p className="text-gray-600 text-sm">
                            We've sent a verification code to your WhatsApp number
                        </p>
                        <p className="text-blue-600 font-medium mt-1">
                            {formData.phone}
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Enter OTP
                            </label>
                            <input
                                ref={otpRef}
                                type="text"
                                value={otp}
                                onChange={handleOtpChange}
                                placeholder="Enter 4-digit OTP"
                                maxLength={4}
                                className="w-full px-4 py-3 text-center text-lg font-medium border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            {otpError && (
                                <p className="text-red-500 text-sm mt-2">{otpError}</p>
                            )}
                        </div>

                        <div className="flex space-x-3">
                            <button
                                onClick={verifyOtp}
                                disabled={otpVerifying || !otp}
                                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                {otpVerifying ? (
                                    <div className="flex items-center justify-center">
                                        <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Verifying...
                                    </div>
                                ) : (
                                    'Verify OTP'
                                )}
                            </button>
                            <button
                                onClick={sendOtp}
                                disabled={otpSending}
                                className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                {otpSending ? (
                                    <div className="flex items-center justify-center">
                                        <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Sending...
                                    </div>
                                ) : (
                                    'Resend'
                                )}
                            </button>
                        </div>

                        <div className="flex justify-center">
                            <button
                                onClick={() => {
                                    setShowOtpModal(false);
                                    setSelectedPaymentMethod('');
                                    setOtp('');
                                    setOtpError('');
                                    setOtpSent(false);
                                    setOtpVerified(false);
                                }}
                                className="text-gray-500 text-sm hover:text-gray-700 transition-colors duration-200"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const PhoneConfirmModal = () => {
        if (!showPhoneConfirmModal) return null;

        const handlePhoneUpdate = () => {
            if (!tempPhone || tempPhone.length < 10) {
            setFormErrors(prev => ({
                ...prev,
                phone: 'Please enter a valid 10-digit phone number',
            }));
            return;
            }

            setFormData(prev => ({
            ...prev,
            phone: tempPhone,
            }));

            setFormErrors(prev => ({
            ...prev,
            phone: '',
            }));
            setIsEditingPhone(false);
        };

        return (
            <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4"
            role="dialog"
            aria-modal="true"
            >
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto">
                <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                    </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Confirm Phone Number
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                    Please confirm that this is your WhatsApp number for Cash on Delivery verification
                </p>
                </div>

                <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2 flex-wrap">
                    <div className="flex items-center mb-2 sm:mb-0">
                        
                        <span className="text-blue-800 font-medium">WhatsApp Number</span>
                    </div>
                    {!isEditingPhone && (
                        <button
                        onClick={() => {
                            setIsEditingPhone(true);
                            setTempPhone(formData.phone);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center transition-colors duration-200"
                        >
                        Edit
                        </button>
                    )}
                    </div>

                    {isEditingPhone ? (
                    <div className="space-y-3">
                        <div>
                        <input
                            type="tel"
                            value={tempPhone}
                            onChange={e => setTempPhone(e.target.value)}
                            placeholder="Enter 10-digit phone number"
                            maxLength="10"
                            className="w-full px-3 py-2 text-center text-lg font-medium border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {formErrors.phone && (
                            <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>
                        )}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:space-x-2 space-y-2 sm:space-y-0">
                        <button
                            onClick={handlePhoneUpdate}
                            className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200"
                        >
                            Update
                        </button>
                        <button
                            onClick={() => {
                            setIsEditingPhone(false);
                            setTempPhone(formData.phone);
                            setFormErrors(prev => ({ ...prev, phone: '' }));
                            }}
                            className="flex-1 border border-gray-300 text-gray-700 py-2 px-3 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors duration-200"
                        >
                            Cancel
                        </button>
                        </div>
                    </div>
                    ) : (
                    <div className="text-center">
                        <p className="text-2xl font-bold text-blue-900 mb-2">{formData.phone}</p>
                        <p className="text-xs text-blue-700">
                        An OTP will be sent to this WhatsApp number for verification
                        </p>
                    </div>
                    )}
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start">
                    <div className="text-yellow-800 text-sm">
                        <p className="font-medium mb-1">Important Notes:</p>
                        <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Make sure this number has WhatsApp installed</li>
                        <li>You must receive the OTP to complete COD order</li>
                        <li>OTP will expire in 5 minutes</li>
                        </ul>
                    </div>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:space-x-3 space-y-3 sm:space-y-0">
                    <button
                    onClick={() => {
                        setShowPhoneConfirmModal(false);
                        setShowOtpModal(true);
                        sendOtp();
                    }}
                    disabled={isEditingPhone}
                    className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                    Yes, Send OTP
                    </button>
                    <button
                    onClick={() => {
                        setShowPhoneConfirmModal(false);
                        setSelectedPaymentMethod('');
                        setIsEditingPhone(false);
                        setFormErrors(prev => ({ ...prev, phone: '' }));
                    }}
                    className="flex-1 border border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200"
                    >
                    Cancel
                    </button>
                </div>
                </div>
            </div>
            </div>
        );
    };


    // Enhanced form field renderer with icons
    const renderFormField = (name, label, type = "text", required = true, icon = null) => (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
                {icon && (
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        {icon}
                    </div>
                )}
                <input
                    type={type}
                    name={name}
                    value={formData[name]}
                    onChange={handleInputChange}
                    className={`w-full ${icon ? 'pl-10' : 'px-4'} py-3 rounded-lg border ${formErrors[name] ? 'border-red-500' : 'border-gray-300'} 
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm`}
                />
            </div>
            {formErrors[name] && (
                <p className="text-red-500 text-sm mt-1">{formErrors[name]}</p>
            )}
        </div>
    );

    const renderOrderSummary = () => (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 sm:px-6 py-4">
                <h2 className="text-lg sm:text-xl font-bold text-white">
                    {translations.checkout.order}
                </h2>
            </div>

            <div className="p-4 sm:p-6">
                {/* Payment Method Section - AT THE TOP */}
                <div className="mb-6">
                    <div className="space-y-4">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
                            Select Payment Method
                        </h3>
                        
                        {/* Billing completion notice */}
                        {!isBillingDetailsComplete() && (
                            <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-900 p-3 sm:p-4 rounded-lg">
                                <div className="flex items-start">
                                    <svg className="hidden md:block h-5 w-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.464 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <div>
                                        <p className="text-sm font-medium">
                                            Please complete all billing details above to select payment method
                                        </p>
                                        <p className="text-xs mt-1">
                                            Required: Name, Address, City, Phone, and Email
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Payment Method Options */}
                        <div className="grid grid-cols-1 gap-4">
                            {/* Online Payment Option */}
                            <label className={`flex flex-col p-3 sm:p-4 border rounded-lg transition-all duration-200 cursor-pointer
                                ${!isBillingDetailsComplete()
                                    ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
                                    : selectedPaymentMethod === 'online'
                                    ? 'border-green-600 bg-green-50 ring-2 ring-green-200'
                                    : 'border-gray-300 bg-white hover:bg-green-50 hover:border-green-300'
                                }`}
                            >
                                <div className="flex items-start space-x-3">
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value="online"
                                        checked={selectedPaymentMethod === 'online'}
                                        onChange={(e) => isBillingDetailsComplete() && setSelectedPaymentMethod(e.target.value)}
                                        disabled={!isBillingDetailsComplete()}
                                        className="h-4 w-4 text-green-600 focus:ring-green-500 disabled:opacity-50 mt-1"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                            <div className="flex items-center mb-2 sm:mb-0">
                                                <div className="mr-3">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-8 sm:w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <span className="text-sm sm:text-base text-gray-900 font-medium">Online Payment</span>
                                                    <p className="text-xs sm:text-sm text-gray-500">Card/UPI/Netbanking</p>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                {isOnlineDiscountAllowed(orderDetails, verifiedProduct) && (
                                                    <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                                        5% OFF
                                                    </span>
                                                )}
                                                <span className="inline-block px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                                                    Free Shipping
                                                </span>
                                            </div>
                                        </div>
                                        
                                        {/* Benefits for Online Payment */}
                                        <div className="mt-3 space-y-1">
                                            {isOnlineDiscountAllowed(orderDetails, verifiedProduct) && (
                                                <div className="flex items-center text-xs sm:text-sm text-green-600">
                                                    <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    <span className="font-medium">5% Instant Discount</span>
                                                </div>
                                            )}
                                            <div className="flex items-center text-xs sm:text-sm text-green-600">
                                                <svg className="hidden md:block lg:block h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span className="font-medium">
                                                    FREE Shipping (Save ₹{orderDetails && Number(orderDetails.totalAmount) >= 1590 ? '120' : '90'})
                                                </span>
                                            </div>
                                            <div className="flex items-center text-xs sm:text-sm text-green-600">
                                                <svg className="hidden md:block lg:block h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span className="font-medium">Secure Payment</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </label>

                            {/* Cash on Delivery Option */}
                            <label className={`flex flex-col p-3 sm:p-4 border rounded-lg transition-all duration-200 cursor-pointer
                                ${!isBillingDetailsComplete()
                                    ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
                                    : selectedPaymentMethod === 'cod'
                                    ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-200'
                                    : 'border-gray-300 bg-white hover:bg-blue-50 hover:border-blue-300'
                                }`}
                            >
                                <div className="flex items-start space-x-3">
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value="cod"
                                        checked={selectedPaymentMethod === 'cod'}
                                        onChange={() => {
                                            if (!isBillingDetailsComplete()) return;
                                            
                                            setSelectedPaymentMethod('cod');

                                            if (ENABLE_COD_OTP) {
                                                if (!formData.phone || formData.phone.length < 10) {
                                                    setFormErrors(prev => ({
                                                        ...prev,
                                                        phone: 'Please enter a valid 10-digit phone number for WhatsApp verification',
                                                    }));
                                                    return;
                                                }
                                                setShowPhoneConfirmModal(true);
                                            } else {
                                                setOtpVerified(true);
                                                setShowPhoneConfirmModal(false);
                                                setShowOtpModal(false);
                                            }
                                        }}
                                        disabled={!isBillingDetailsComplete()}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 disabled:opacity-50 mt-1"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                            <div className="flex items-center mb-2 sm:mb-0">
                                                <div className="mr-3">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <span className="text-sm sm:text-base text-gray-900 font-medium">Cash on Delivery</span>
                                                    <p className="text-xs sm:text-sm text-gray-500">Pay when you receive</p>
                                                    {ENABLE_COD_OTP && (
                                                        <p className="text-blue-600 text-xs mt-1">WhatsApp verification required</p>
                                                    )}
                                                    {selectedPaymentMethod === 'cod' && otpVerified && (
                                                        <p className="text-green-600 text-xs sm:text-sm mt-1 flex items-center">
                                                            <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                            Phone number verified
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-xs sm:text-sm text-orange-600 font-medium">
                                                +₹{orderDetails && Number(orderDetails.totalAmount) >= 1590 ? '120' : '90'} shipping
                                            </div>
                                        </div>
                                        
                                        {/* Information for COD */}
                                        <div className="mt-3 space-y-1">
                                            <div className="flex items-center text-xs sm:text-sm text-gray-600">
                                                <svg className="hidden md:block lg:block h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span>Pay at your doorstep</span>
                                            </div>
                                            <div className="flex items-center text-xs sm:text-sm text-orange-600">
                                                <svg className="hidden md:block lg:block h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                                </svg>
                                                <span className="font-medium">
                                                    ₹{orderDetails && Number(orderDetails.totalAmount) >= 1590 ? '120' : '90'} Shipping Charge
                                                </span>
                                            </div>
                                            <div className="flex items-center text-xs sm:text-sm text-gray-500">
                                                <svg className="hidden md:block lg:block h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span>No online discounts</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
                
                {/* Order Review Section */}
                <div className="mb-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4">Order Review</h3>
                    <div className="space-y-3 sm:space-y-4">
                        {verifiedProduct && verifiedProduct.map((product, index) => {
                            // Product image logic
                            try {
                                const recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewed')) || [];
                                const matchingProduct = recentlyViewed.find(item => 
                                    item.name === product.name || (product.id && item.id === product.id)
                                );
                                
                            } catch (error) {
                                console.error('Error retrieving product image:', error);
                            }

                            return (
                                <div key={index} className="flex items-start space-x-3 sm:space-x-4 border-b border-gray-200 pb-3 sm:pb-4 last:border-b-0">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                                            <div className="min-w-0 flex-1">
                                                <h4 className="text-sm sm:text-base font-medium text-gray-800 truncate">
                                                    {product.name}
                                                </h4>
                                                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                                                    Quantity: {product.quantity}
                                                </p>
                                            </div>
                                            <div className="flex items-center justify-between sm:justify-end space-x-3">
                                                <div className="text-sm sm:text-base font-medium text-gray-900">
                                                    {currentCurrency.symbol}{((product.price) * currentCurrency.rate).toFixed(2)}
                                                </div>
                                                <button
                                                    onClick={() => handleRemoveProduct(index)}
                                                    className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                                                    title="Remove item"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Free Products Section */}
                {showFreeProducts() && (
                    <div className="mb-6 relative">
                        <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 bg-red-600 text-white px-2 py-1 sm:px-4 sm:py-1 rounded-full transform rotate-12 z-10 shadow-lg">
                            <span className="text-xs sm:text-sm font-bold">Special Offer!</span>
                        </div>
                        
                        <div className="p-3 sm:p-4 bg-gradient-to-r from-orange-100 to-amber-100 rounded-lg border border-orange-200">
                            <div className="flex items-center mb-3">
                                <div className="text-2xl sm:text-3xl mr-3">🎁</div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-bold text-orange-700">
                                        FREE Products Included!
                                    </h3>
                                    <p className="text-xs sm:text-sm text-orange-700">
                                        Worth ₹{calculateFreeProductsValue().toFixed(2)}!
                                    </p>
                                </div>
                            </div>
                            
                            <div className="bg-white/50 rounded-lg p-2 sm:p-3 mb-3">
                                <p className="text-xs sm:text-sm text-orange-800 font-medium">
                                    Complete your purchase to receive these premium gifts:
                                </p>
                            </div>
                            
                            <div className="space-y-2 sm:space-y-3">
                                {getFreeProducts().map((item, idx) => (
                                    <div key={idx} className="flex items-center bg-white p-2 sm:p-3 rounded-md border border-orange-200 shadow-sm">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs sm:text-sm font-medium text-gray-800 truncate">
                                                {item.name}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {item.description || "Premium gift with your purchase"}
                                            </p>
                                            {item.originalValue && (
                                                <p className="text-xs text-orange-600 font-medium mt-1">
                                                    Value: ₹{item.originalValue}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            <div className="mt-3 bg-orange-100 p-2 sm:p-3 rounded-md text-xs sm:text-sm border border-orange-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center text-orange-800">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="font-medium">Limited time offer!</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Price Breakdown */}
                <div className="space-y-3 sm:space-y-4 mb-6">
                    {/* Subtotal */}
                    <div className="flex justify-between items-center py-2">
                        <span className="text-sm sm:text-base font-medium text-gray-700">Subtotal</span>
                        <span className="text-sm sm:text-base font-medium text-gray-700">
                            {currentCurrency.symbol}{(orderDetails.totalAmount * currentCurrency.rate).toFixed(2)}
                        </span>
                    </div>

                    {/* Promo Code Discount */}
                    {/* {isPromoApplied && (
                        <div className="flex justify-between items-center py-2">
                            <span className="text-sm sm:text-base font-medium text-green-600">
                            Promo Discount (50%)
                            </span>
                            <span className="text-sm sm:text-base font-medium text-green-600">
                            -{currentCurrency.symbol}{((orderDetails.totalAmount * currentCurrency.rate) * 0.5).toFixed(2)}
                            </span>
                        </div>
                    )} */}

                    {/* Online Payment Discount */}
                    {selectedPaymentMethod === 'online' && isOnlineDiscountAllowed(orderDetails, verifiedProduct) && (
                        <div className="flex justify-between items-center py-2">
                            <span className="text-sm sm:text-base font-medium text-green-600">
                                Online Payment Discount (5%)
                            </span>
                            <span className="text-sm sm:text-base font-medium text-green-600">
                                {/* -{currentCurrency.symbol}{(((orderDetails.totalAmount * currentCurrency.rate) * (isPromoApplied ? 0.5 : 1)) * 0.05).toFixed(2)} */}
                                -{currentCurrency.symbol}{(((orderDetails.totalAmount * currentCurrency.rate)) * 0.05).toFixed(2)}
                            </span>
                        </div>
                    )}

                    {/* Shipping Charge */}
                    <div className="flex justify-between items-center py-2">
                        <span className="text-sm sm:text-base font-medium text-gray-700">
                            {translations.checkout.shipping}
                        </span>
                        <span className={`text-sm sm:text-base font-medium ${selectedPaymentMethod === 'online' ? "text-green-600" : "text-gray-700"}`}>
                            {selectedPaymentMethod === 'online' ? "Free" : 
                                `${currentCurrency.symbol}${((orderDetails && Number(orderDetails.totalAmount) >= 1590 ? 120 : 90) * currentCurrency.rate).toFixed(2)}`}
                        </span>
                    </div>

                    {/* Promo Code Section */}
                    <div className="py-3">
                        <button 
                            type="button"
                            onClick={() => document.getElementById('promo-section').classList.toggle('hidden')}
                            className="text-blue-600 hover:text-blue-800 text-sm flex items-center w-full sm:w-auto"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Add promo code
                        </button>
                        
                        <div id="promo-section" className="hidden mt-3">
                            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                                <input 
                                    type="text" 
                                    value={promoCode}
                                    onChange={e => setPromoCode(e.target.value)}
                                    placeholder="Enter promo code"
                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                    type="button"
                                    onClick={handlePromoCodeApply}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
                                >
                                    Apply
                                </button>
                            </div>
                            {formErrors.promoCode && (
                                <p className="text-red-500 text-xs mt-1">{formErrors.promoCode}</p>
                            )}
                            {isPromoApplied && (
                                <p className="text-green-600 text-xs mt-1">Promo code applied successfully!</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Order Confirmation Checkbox */}
                <div className="py-4 border-t border-gray-200">
                    <label className="flex items-start space-x-2">
                        <input
                            type="checkbox"
                            className="form-checkbox h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mt-0.5 rounded focus:ring-blue-500"
                            required
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                confirmedOrder: e.target.checked
                            }))}
                        />
                        <span className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                            I confirm that the product details and order information are correct
                        </span>
                    </label>
                </div>

                {/* Total Section */}
                {selectedPaymentMethod && (
                    <div className="flex justify-between items-center py-4 border-t border-gray-200">
                        <span className="text-lg sm:text-xl font-bold text-gray-800">
                            {translations.checkout.total}
                        </span>
                        <div className="text-right">
                            {(selectedPaymentMethod === 'online' && isOnlineDiscountAllowed(orderDetails, verifiedProduct)) && (
                                <div className="text-xs sm:text-sm text-gray-400 line-through mb-1">
                                    Original: {currentCurrency.symbol}{(() => {
                                        const originalAmount = orderDetails.totalAmount * currentCurrency.rate;
                                        const shippingCharge = selectedPaymentMethod === 'cod' ? 
                                            (Number(orderDetails.totalAmount) >= 1590 ? 120 : 90) : 0;
                                        const withShipping = originalAmount + shippingCharge;
                                        return withShipping.toFixed(2);
                                    })()}
                                </div>
                            )}
                            <span className="text-lg sm:text-xl font-bold text-gray-800">
                                {currentCurrency.symbol}{convertedAmount}
                            </span>
                        </div>
                    </div>
                )}

                {/* Show message when no payment method is selected */}
                {!selectedPaymentMethod && (
                    <div className="flex justify-between items-center py-4 border-t border-gray-200">
                        <span className="text-lg sm:text-xl font-bold text-gray-800">Total</span>
                        <div className="text-right">
                            <span className="text-sm sm:text-lg font-medium text-gray-500">
                                Select payment method to view total
                            </span>
                        </div>
                    </div>
                )}

                {/* Submit Button */}
                <button
                    type="submit"
                    onClick={handleSubmit}
                    disabled={
                        isSubmitting ||
                        processingPayment ||
                        !isBillingDetailsComplete() ||
                        !selectedPaymentMethod ||
                        (ENABLE_COD_OTP && selectedPaymentMethod === 'cod' && !otpVerified)
                    }
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium py-3 sm:py-4 px-6 rounded-lg
                        transition-all duration-200 transform hover:scale-105 hover:shadow-lg
                        disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none
                        text-sm sm:text-base"
                >
                    {(isSubmitting || processingPayment) ? (
                        <div className="flex items-center justify-center">
                            <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 mr-3" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            {translations.checkout.processing}
                        </div>
                    ) : !isBillingDetailsComplete() ? (
                        <span>Complete Billing Details First</span>
                    ) : !selectedPaymentMethod ? (
                        <span>Select Payment Method</span>
                    ) : selectedPaymentMethod === 'cod' && !otpVerified ? (
                        <span>Verify Phone Number First</span>
                    ) : (
                        <div className="flex items-center justify-center">
                            <span>{translations.checkout.order}</span>
                            <span className="ml-2">→</span>
                        </div>
                    )}
                </button>
            </div>
        </div>
    );

    // Add these helper functions before the return statement
    const getFreeProducts = () => {
        const freeItems = [];
         if (isPromoApplied && hasKarungaliMala()) {
            freeItems.push({
            name: 'Karungali Bracelet',
            description: 'Complimentary with Karungali Mala (Promo: KARUNGALI)',
            // omit originalValue to avoid showing a “worth ₹X” reduction; this is purely a free gift
            });
        }
        
        try {
            // Get cart items from localStorage
            const cartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
            
            // Check each cart item for free accessories
            cartItems.forEach(item => {
                if (item.freeAccessories && item.freeAccessories.length > 0) {
                    // Add estimated value to each free accessory if not present
                    const accessoriesWithValue = item.freeAccessories.map(accessory => ({
                        ...accessory,
                        // Add approximate value for free items if not provided (creates more perceived value)
                        originalValue: accessory.originalValue || Math.floor(Math.random() * 300) + 200
                    }));
                    
                    freeItems.push(...accessoriesWithValue);
                }
            });
            
            // Also check the current order details if available
            if (orderDetails && orderDetails.items) {
                orderDetails.items.forEach(item => {
                    try {
                        // Check recently viewed products for more information
                        const recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewed')) || [];
                        const foundProduct = recentlyViewed.find(p => p.name === item.name || p.id === item.id);
                        
                        if (foundProduct && foundProduct.freeAccessories && foundProduct.freeAccessories.length > 0) {
                            // Add estimated value to each free accessory if not present
                            const accessoriesWithValue = foundProduct.freeAccessories.map(accessory => ({
                                ...accessory,
                                originalValue: accessory.originalValue || Math.floor(Math.random() * 300) + 200
                            }));
                            
                            freeItems.push(...accessoriesWithValue);
                        }
                    } catch (error) {
                        console.error('Error checking for free accessories:', error);
                    }
                });
            }
        } catch (error) {
            console.error('Error getting free products:', error);
        }
        
        // Remove duplicates based on name
        const uniqueItems = [];
        const seenNames = new Set();
        
        freeItems.forEach(item => {
            if (!seenNames.has(item.name)) {
                seenNames.add(item.name);
                uniqueItems.push(item);
            }
        });
        
        return uniqueItems;
    };

    const calculateFreeProductsValue = () => {
        return getFreeProducts().reduce((total, item) => {
            return total + (item.originalValue || 0);
        }, 0);
    };

    const showFreeProducts = () => {
        return getFreeProducts().length > 0;
    };

    // Add a countdown timer function for more urgency
    const getCountdownTime = () => {
        // Generate a random time - changes on each page load for more urgency
        const hours = Math.floor(Math.random() * 5) + 1; // 1-6 hours
        const minutes = Math.floor(Math.random() * 60); // 0-59 minutes
        
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} remaining`;
    };

    // Add this style to the component to support animations
    React.useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            @keyframes pulse-slow {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.85; }
            }
            .animate-pulse-slow {
                animation: pulse-slow 3s infinite;
            }
            
            @keyframes wiggle {
                0%, 100% { transform: rotate(-3deg); }
                50% { transform: rotate(3deg); }
            }
            .wiggle-animation {
                animation: wiggle 5s infinite;
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    if (!orderDetails) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }
    // Excluded product(s) for online 5% discount (case-insensitive)
    const EXCLUDED_ONLINE_DISCOUNT_PRODUCTS = new Set(["sacred karungali twin blessings set",]);

    // Returns true if the 5% online discount is allowed for current order
    const isOnlineDiscountAllowed = (orderDetails, verifiedProduct) => {
    const hasExcluded = () => {
        const match = (name) =>
        typeof name === "string" &&
        EXCLUDED_ONLINE_DISCOUNT_PRODUCTS.has(name.trim().toLowerCase());

        if (Array.isArray(verifiedProduct) && verifiedProduct.some(p => match(p?.name))) return true;
        if (orderDetails?.productName && match(orderDetails.productName)) return true;
        if (Array.isArray(orderDetails?.items) && orderDetails.items.some(i => match(i?.name))) return true;
        return false;
    };

    return !hasExcluded();
    };
    const SITE_URL = 'https://sacredrelm.com';

    // CheckoutPage Schema
    const checkoutPageSchema = {
        "@context": "https://schema.org",
        "@type": "CheckoutPage",
        "name": "Checkout - Sacred Relm",
        "description": "Complete your purchase of authentic Karungali and spiritual products.",
        "url": `${SITE_URL}/billing`
    };

    // Breadcrumb Schema
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
        {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": SITE_URL
        },
        {
            "@type": "ListItem",
            "position": 2,
            "name": "Shop",
            "item": `${SITE_URL}/shop`
        },
        {
            "@type": "ListItem",
            "position": 3,
            "name": "Checkout",
            "item": `${SITE_URL}/billing`
        }
        ]
    };



    return (
        <div>
        <Helmet>
            <title>Checkout - Sacred Relm</title>
            <meta name="description" content="Complete your purchase securely at Sacred Relm. Multiple payment options available." />
            <link rel="canonical" href={`${SITE_URL}/billing`} />
            <meta name="robots" content="noindex, nofollow" />
            
            {/* CheckoutPage Schema */}
            <script type="application/ld+json">
            {JSON.stringify(checkoutPageSchema)}
            </script>
            
            {/* Breadcrumb Schema */}
            <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
            </script>
        </Helmet>
        
            {/* Hero banner */}
            <div className="bg-gradient-to-r from-blue-700 to-indigo-800 py-12 mb-8">
                <div className="px-4 sm:px-6 lg:px-8 flex flex-col items-center">
                    <h1 className="text-4xl font-extrabold text-white mb-2">
                        {translations.checkout.title}
                    </h1>
                    <p className="text-blue-100 text-center max-w-md">
                        Complete your purchase securely with our easy checkout process
                    </p>
                    
                    {/* Shopping cart summary badge */}
                    <div className="mt-8 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                        <div className="flex items-center text-white">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                            <span>
                                {verifiedProduct?.length || 1} item{(verifiedProduct?.length || 1) > 1 ? 's' : ''} | 
                                <span className="font-semibold ml-1">{currentCurrency.symbol} {convertedAmount}</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-4 py-8 mb-16">
                {/* Checkout steps indicator */}
                <CheckoutSteps />

                {formErrors.submit && (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg mb-8 flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 10-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd" />
                        </svg>
                        <span>{formErrors.submit}</span>
                    </div>
                )}

                <div className="grid lg:grid-cols-12 gap-8">
                    {/* Billing form */}
                    <div className="lg:col-span-7 space-y-8">
                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="flex items-center mb-6">
                                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                                <h2 className="text-2xl font-bold text-gray-800">
                                    {translations.checkout.sectitle}
                                </h2>
                            </div>

                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    {renderFormField("firstName", translations.checkout.firstname, "text", true, 
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    )}
                                    {renderFormField("lastName", translations.checkout.lastname, "text", true,
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    )}
                                </div>

                                {/* Divider */}
                                <div className="relative py-2">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-200"></div>
                                    </div>
                                    <div className="relative flex justify-center">
                                        <span className="bg-white px-4 text-sm text-gray-500">Location Information</span>
                                    </div>
                                </div>

                                {/* Country selector with icon */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        {translations.checkout.country}<span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2v8a2 2 0 002 2zm9-13.5V9" />
                                            </svg>
                                        </div>
                                        <select
                                            name="country"
                                            value={formData.country}
                                            onChange={handleInputChange}
                                            className="w-full pl-10 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                                        >
                                            {Object.keys(COUNTRY_CURRENCY_MAP).map(country => (
                                                <option key={country} value={country}>
                                                    {country} ({COUNTRY_CURRENCY_MAP[country].currency})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {renderFormField("streetAddress", translations.checkout.address, "text", true,
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                )}
                                {renderFormField("apartment", translations.checkout.clientaddress, "text", false,
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                )}
                                {renderFormField("townCity", translations.checkout.city, "text", true,
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                )}

                                {/* Divider */}
                                <div className="relative py-2">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-200"></div>
                                    </div>
                                    <div className="relative flex justify-center">
                                        <span className="bg-white px-4 text-sm text-gray-500">Contact Information</span>
                                    </div>
                                </div>

                                {renderFormField("phone", translations.checkout.phone, "tel", true,
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 002-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                )}
                                {renderFormField("email", translations.checkout.email, "email", true,
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Order summary */}
                    <div className="lg:col-span-5">
                        {renderOrderSummary()}
                    </div>
                </div>
            </div>
            {ENABLE_COD_OTP && <OtpModal />}
            {ENABLE_COD_OTP && <PhoneConfirmModal />}
            <PromoToast />
        </div>
    );
};

export default Checkout;